'use client';

import {BubbleMenu, useCurrentEditor} from '@tiptap/react';
import {Bold, Check, Italic, LinkIcon, Strikethrough, X} from 'lucide-react';
import {useState} from 'react';

import {ButtonProps} from '../../components';
import {cn} from '../../libs';

const BubbleButton = ({className, children, ...props}: ButtonProps) => {
  return (
    <button
      {...props}
      className={cn(
        'rounded-sm bg-bg-secondary p-2 text-paragraph-sm *:text-text-muted hover:bg-bg-secondary-hover',
        className,
      )}
      type="button"
    >
      {children}
    </button>
  );
};

export const InlineToolbar = () => {
  const {editor} = useCurrentEditor();
  const [isURLEditing, setIsURLEditing] = useState(false);
  const [url, setUrl] = useState('');

  const addLink = () => {
    if (url) {
      editor?.chain().focus().setLink({href: url}).run();
      setIsURLEditing(false);
      setUrl('');
    }
  };

  const removeLink = () => {
    editor?.chain().focus().unsetLink().run();
    setIsURLEditing(false);
    setUrl('');
  };

  const editURL = () => {
    const attrs = editor?.getAttributes('link');
    setUrl(attrs?.href || '');
    setIsURLEditing(true);
  };

  return (
    <BubbleMenu
      className="flex items-center gap-1 rounded-lg border bg-bg-secondary p-1"
      tippyOptions={{
        onHide: () => {
          setIsURLEditing(false);
          setUrl('');
        },
        hideOnClick: true,
      }}
      editor={editor}
    >
      {isURLEditing ? (
        <div className="flex items-center gap-1">
          <input
            className="rounded border border-border bg-background px-2 py-1 text-sm"
            // biome-ignore lint/a11y/noAutofocus: skip
            autoFocus={true}
            type="text"
            placeholder="Введите ссылку"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addLink()}
          />
          <BubbleButton onClick={addLink}>
            <Check className="size-4" />
          </BubbleButton>
          <BubbleButton onClick={removeLink}>
            <X className="size-4" />
          </BubbleButton>
        </div>
      ) : (
        <>
          <BubbleButton
            className={editor?.isActive('bold') ? 'font-bold' : ''}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold className="size-4" />
          </BubbleButton>
          <BubbleButton
            className={editor?.isActive('italic') ? 'font-bold' : ''}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic className="size-4" />
          </BubbleButton>
          <BubbleButton
            className={editor?.isActive('strike') ? '*:text-text-primary' : ''}
            onClick={() => editor?.chain().focus().toggleStrike().run()}
          >
            <Strikethrough className="size-4" />
          </BubbleButton>
          <BubbleButton
            onClick={editURL}
            className={editor?.getAttributes('link')?.href ? '*:text-text-primary' : ''}
          >
            <LinkIcon className="size-4" />
          </BubbleButton>
        </>
      )}
    </BubbleMenu>
  );
};
